import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserService } from '../../services/user.service';
import { interval, Subscription } from 'rxjs';

type TargetUser = ReturnType<UserService['getTargetUser']>;
type Impersonator = ReturnType<UserService['getImpersonator']>;

@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './impersonation-banner.component.html',
  styleUrls: ['./impersonation-banner.component.scss']
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  isImpersonating = false;
  sessionExpired = false;
  targetUser: TargetUser = null;
  impersonator: Impersonator = null;
  private readonly subscriptions = new Subscription();
  private ending = false;

  constructor(private userService: UserService) {}

  ngOnInit() {
    this.updateImpersonationStatus();

    // UserService updates its state synchronously before emitting on any of
    // these monitors (see login() / logout()), so no settle delay is needed.
    this.subscriptions.add(
      this.userService.getLogInMonitor().subscribe(() => this.updateImpersonationStatus())
    );
    this.subscriptions.add(
      this.userService.getSessionMonitor().subscribe(() => this.updateImpersonationStatus())
    );
    // A normal Log out must clear the banner too, not only endImpersonation().
    this.subscriptions.add(
      this.userService.getLogOutMonitor().subscribe(() => this.updateImpersonationStatus())
    );

    // Poll so an expiry is noticed even without navigation or a login event.
    this.subscriptions.add(
      interval(30000).subscribe(() => this.updateImpersonationStatus())
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  updateImpersonationStatus() {
    if (!this.userService.isLoggedIn()) {
      // Logged out: drop any stale impersonation state so the banner hides.
      this.isImpersonating = false;
      this.sessionExpired = false;
      this.targetUser = null;
      this.impersonator = null;
      this.ending = false;
      return;
    }

    this.isImpersonating = this.userService.isImpersonating();
    this.sessionExpired = this.userService.isSessionExpired();

    if (this.isImpersonating && this.sessionExpired) {
      // Same as AuthGuard on navigation: an expired impersonation ends itself
      // (logout + return to the admin area), which is what the expired copy
      // promises. `ending` stops the poll from firing it more than once.
      if (!this.ending) {
        this.ending = true;
        this.userService.endImpersonation();
      }
      return;
    }

    if (this.isImpersonating) {
      this.targetUser = this.userService.getTargetUser();
      this.impersonator = this.userService.getImpersonator();
    }
    // The body / ion-app `impersonating` classes are owned by AppComponent
    // (updateImpersonationBodyClass), which toggles them on login and logout.
  }

  endImpersonation() {
    this.userService.endImpersonation();
  }
}
